import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { deleteFromFtp } from "../../../lib/deleteFromFtp";

interface Engine {
  id: string;
  title: string;
  description: string;
  price: string;
  imageUrl: string;
  brand: string;
}

const EngineList: React.FC = () => {
  const [engines, setEngines] = useState<Engine[]>([]);
  const [filteredEngines, setFilteredEngines] = useState<Engine[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchEngines = async () => {
    const { data, error } = await supabase.from('engines').select('*');
    if (error) {
        console.error(error);
    } else {
        setEngines(data as Engine[]);
        setFilteredEngines(data as Engine[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEngines();
  }, []);

  useEffect(() => {
    const results = engines.filter((item) =>
      item.title.toLowerCase().includes(search.toLowerCase()) || 
      item.brand.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredEngines(results);
  }, [search, engines]);

  const handleDelete = async (id: string) => {
      if(!window.confirm("Bu motoru silmek istediğinize emin misiniz?")) return;
      
      const itemToDelete = engines.find(e => e.id === id);

      try {
          const { error } = await supabase.from('engines').delete().eq('id', id);
          if (!error) {
              if (itemToDelete?.imageUrl) deleteFromFtp(itemToDelete.imageUrl);
              fetchEngines();
          } else {
              console.error(error);
          }
      } catch (error) {
          console.error(error);
      }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Motor Yönetimi</h1>
          <p className="text-gray-500">Tüm motorları yönetin</p>
        </div>
        <Link
          to="/panel/engines/add"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center shadow-sm transition-all"
        >
          <Plus className="w-5 h-5 mr-2" />
          Yeni Motor Ekle
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Motor ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
        ) : filteredEngines.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Motor bulunamadı.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 text-gray-600 font-medium text-sm">
                <tr>
                  <th className="p-4">Görsel</th>
                  <th className="p-4">Model</th>
                  <th className="p-4">Marka</th>
                  <th className="p-4">Fiyat</th>
                  <th className="p-4">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEngines.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="w-12 h-12 rounded bg-gray-200 overflow-hidden">
                         <img 
                           src={item.imageUrl} 
                           alt={item.title} 
                           className="w-full h-full object-cover"
                           onError={(e) => {(e.target as HTMLImageElement).src = '/logo.svg'}} 
                         />
                      </div>
                    </td>
                    <td className="p-4 font-medium text-gray-800">{item.title}</td>
                    <td className="p-4 text-gray-600">
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">{item.brand}</span>
                    </td>
                    <td className="p-4 text-green-600 font-semibold">{item.price}</td>
                    <td className="p-4 flex gap-2">
                        <Link to={`/panel/engines/edit/${item.id}`} className="text-blue-500 hover:text-blue-700 p-2 hover:bg-blue-50 rounded">
                            <span className="sr-only">Düzenle</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                        </Link>
                        <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded">
                            <Trash2 size={20} />
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EngineList;
